import { useCallback, useMemo } from 'react';
import { useKeepArrayIdentity } from 'src/hooks/useKeepIdentity';
import { usePersistedJSON } from 'src/hooks/usePersistedJSON';

import {
  DEFAULT_DISPLAY_METRICS,
  DisplayMetric,
  effectiveDisplayMetrics,
  isDisplayMetricArray,
  isVehicleListMetric,
  migrateLegacyDisplayMetrics,
  PersistedDisplayMetric,
} from './displayMetricKeys';

/**
 * 表示項目の localStorage キー。/map と convenience が同じ設定を共有する。
 * 変えると既存ユーザーの保存値が読めなくなるので変更しない。
 */
export const DISPLAY_METRICS_STORAGE_KEY = 'convenienceDispatch.displayMetrics';

/**
 * 表示項目を分けて持つ単位 (log_web_issues#338 C7)。長距離配車は最大 10 日を 1 画面に
 * 出すので、日数によって出したい項目が変わる（週は 1 日ぶんの列が細く、数字より地点が要る）。
 *
 * Why not 表示日数そのもの (1〜10 の 10 区分): 4 日から 5 日へ広げただけで設定が空になる。
 * Why not 「単日 / 複数日」の 2 区分: ツールバーの選択肢が 1日 / 3日 / 週（7日）の 3 つ
 * (LONG_HAUL_SPAN_OPTIONS) で、3 日と週では列幅が倍以上違う。選択肢と区分を合わせる。
 */
export type DisplayMetricScope = '1d' | '3d' | 'week';

/**
 * 区分ごとの保存先。
 *
 * Why 1 日は既存キーのまま: 1 日を開いた盤面という点で /map・カード配車と同じものなので、
 * 設定も同じものが効くほうが一貫する。移行コードも要らず、既存ユーザーの保存値が
 * そのまま 1 日表示に効く。別キーにすると、長距離を 1 日で開いたときだけ設定が
 * 初期値に戻り「消えた」ように見える。
 *
 * Why not '7d': 日付ピッカーは 10 日まで選べるので、週の区分は 7〜10 日を含む。
 * '7d' はキー名が実態と食い違う。ツールバーの表示（週（7日））に合わせて week にする。
 */
export const DISPLAY_METRICS_SCOPED_STORAGE_KEYS: Record<DisplayMetricScope, string> = {
  '1d': DISPLAY_METRICS_STORAGE_KEY,
  '3d': `${DISPLAY_METRICS_STORAGE_KEY}.3d`,
  week: `${DISPLAY_METRICS_STORAGE_KEY}.week`,
};

/**
 * 区分ごとの既定値。
 *
 * 3 日・週はサンプル (route-planner MatchingPresenter/index.tsx:326-330) と同じく積地・降地を
 * 初期表示にする。粗利を足すのは、外すと effectiveDisplayMetrics が車両タグの既定
 * (納品タグ・車両タイプ・粗利) を補い、1 日のときと車両リストの見え方が変わってしまうため。
 *
 * Why not サンプルの週の既定（数量を外す）をそのまま写す: effectiveDisplayMetrics は
 * 数値指標が 1 つも無い保存値に quantity を補う契約なので、保存値から数量を外しても
 * 実効値には戻ってくる。「保存値だけ違って画面は同じ」になるため、実際に効く形で書く。
 * その結果 3 日と週の既定は同じものになる（この機能の値は、利用者が区分ごとに変えた
 * 選択を覚えておくことの方にある）。
 */
const SCOPED_DEFAULT_DISPLAY_METRICS: Record<DisplayMetricScope, DisplayMetric[]> = {
  '1d': DEFAULT_DISPLAY_METRICS,
  '3d': [...DEFAULT_DISPLAY_METRICS, 'origin', 'destination'],
  week: [...DEFAULT_DISPLAY_METRICS, 'origin', 'destination'],
};

/**
 * 表示日数（両端を含む）から区分を決める。境界の取り方はサンプルの
 * `_totalDaysRange >= 7 ? '7d' : >= 3 ? '3d' : '1d'` と同じ。
 *
 * 読めない期間 (null) を週にするのは、URL 直打ちで壊れた期間のまま触ったときに
 * 既存 2 画面と共有している 1 日のキーへ書き込ませないため。長距離を開く既定も 7 日
 * (LONG_HAUL_DEFAULT_SPAN_DAYS)。
 */
export function displayMetricScopeOf(spanDays: number | null): DisplayMetricScope {
  if (spanDays == null) return 'week';
  if (spanDays >= 7) return 'week';
  return spanDays >= 3 ? '3d' : '1d';
}

/**
 * 表示項目の永続化 + 実効 Set の導出。キー・既定値・バリデータの 3 点セットに加え、
 * effectiveDisplayMetrics を通した Set (第3要素) までここで返す。
 *
 * 実効 Set まで hook が返す理由: 各 Presenter が effectiveDisplayMetrics(displayMetrics)
 * を自分で呼んで Set を組む形だと、呼び忘れの余地が残る。実際に Map/ConvenienceDispatches
 * の両 Presenter で呼び忘れが起き、保存値が数値指標ゼロ (粗利だけ等) のとき未割当カードの
 * 数量だけ出ない不具合が2回発生した。「hook が返したものを使うだけ」にすることで、
 * Presenter 側に導出を書く余地そのものを無くす。
 *
 * 第1要素（生の保存値）と第2要素（setter）は変更前と同じもの。DisplayMetricToggle には
 * 引き続き生の値を渡す。実効配列を渡すとトグル操作で quantity が保存値に書き込まれ、
 * 「保存値は書き換えない」という非破壊方針が壊れるため。
 *
 * useMemo で包む理由: 包まないと毎レンダーで新しい Set 参照ができ、displayMetrics が
 * 変わっていなくても memo 化された下流コンポーネント (ConvenienceDispatchVehicleList 等)
 * の再レンダー抑止が効かなくなる。
 *
 * `scope` は表示日数の区分 (log_web_issues#338 C7)。既定の '1d' は従来のキーそのものなので、
 * 引数を渡さない /map・カード配車の挙動は変わらない。
 */
export function useDisplayMetrics(scope: DisplayMetricScope = '1d'): readonly [
  DisplayMetric[],
  (metrics: DisplayMetric[]) => void,
  Set<DisplayMetric>,
  Set<DisplayMetric>,
] {
  // 3 区分ぶんを同時に持つ。
  // Why not usePersistedJSON へ区分ごとのキーを渡して 1 本で済ませる: あちらは初回 render の
  // 遅延初期化でしか localStorage を読まないので、キーだけ差し替えても値は読み直されない。
  // 週から 3 日へ切り替えても週の値が出たままになる（書き込み先だけが新しいキーへ動く）。
  const oneDay = usePersistedJSON<PersistedDisplayMetric[]>(
    DISPLAY_METRICS_SCOPED_STORAGE_KEYS['1d'],
    SCOPED_DEFAULT_DISPLAY_METRICS['1d'],
    isDisplayMetricArray,
  );
  const threeDays = usePersistedJSON<PersistedDisplayMetric[]>(
    DISPLAY_METRICS_SCOPED_STORAGE_KEYS['3d'],
    SCOPED_DEFAULT_DISPLAY_METRICS['3d'],
    isDisplayMetricArray,
  );
  const week = usePersistedJSON<PersistedDisplayMetric[]>(
    DISPLAY_METRICS_SCOPED_STORAGE_KEYS.week,
    SCOPED_DEFAULT_DISPLAY_METRICS.week,
    isDisplayMetricArray,
  );
  const [storedMetrics, setStoredMetrics] = scope === 'week' ? week : (scope === '3d' ? threeDays : oneDay);
  // 旧キーの読み替えは保存値の入口 1 か所で済ませる。各画面で読み替えると、
  // トグルの onChange が読み替え前の配列を基準に書き戻して旧キーが復活する。
  const displayMetrics = useMemo(() => migrateLegacyDisplayMetrics(storedMetrics), [storedMetrics]);
  // 書き込み側は旧キーを受け取らない（読み替え済みの値しか渡ってこない）
  const setDisplayMetrics = useCallback(
    (metrics: DisplayMetric[]) => setStoredMetrics(metrics),
    [setStoredMetrics],
  );
  const displayMetricSet = useMemo(
    () => new Set(effectiveDisplayMetrics(displayMetrics)),
    [displayMetrics],
  );

  // 左のトラックリスト向けの実効集合。中身（並びも含む）が変わらない限り参照を据え置く。
  // 実効集合をそのまま渡すと、未割当リストにしか効かない項目のトグルでも参照が変わり、
  // 全トラック行の memo と /map の buildExpandedRowVMByRowId が無駄に走る。
  // 並びまで見る useKeepArrayIdentity を使うのは、VehicleInfoView が「最後にトグルした
  // 数値指標」をゲージの対象にするため（順序を無視すると古い指標のまま据え置かれる）。
  const keepVehicleMetrics = useKeepArrayIdentity<DisplayMetric>();
  const vehicleMetricList = useMemo(
    () => keepVehicleMetrics(effectiveDisplayMetrics(displayMetrics).filter(isVehicleListMetric)),
    [displayMetrics, keepVehicleMetrics],
  );
  const vehicleDisplayMetricSet = useMemo(() => new Set(vehicleMetricList), [vehicleMetricList]);

  return [displayMetrics, setDisplayMetrics, displayMetricSet, vehicleDisplayMetricSet] as const;
}
